import React, { useState, useEffect } from 'react';
// FIX: Replaced missing storageService with apiService
import { apiService } from '../services/apiService';
// FIX: Replaced missing storage.interface with types.ts, add ALL_EXPORTABLE_COLUMNS
import { EXPORT_COLUMNS_PREF_KEY, getDefaultExportColumnSettings, ExportColumnSetting, ALL_EXPORTABLE_COLUMNS } from '../types';
import { Spinner } from './Spinner';
import { useAuth } from '../hooks/useAuth';

interface ExportSettingsManagerProps {
    title: string;
}

export const ExportSettingsManager: React.FC<ExportSettingsManagerProps> = ({ title }) => {
    const [settings, setSettings] = useState<ExportColumnSetting[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        const loadSettings = async () => {
            if (!user) return;
            setIsLoading(true);

            const savedSettings = await apiService.getPreference<ExportColumnSetting[]>(EXPORT_COLUMNS_PREF_KEY, null);

            if (savedSettings) {
                const savedSettingsMap = new Map(savedSettings.map(s => [s.key, s]));
                
                const mergedSettings = ALL_EXPORTABLE_COLUMNS.map(col => {
                    const saved = savedSettingsMap.get(col.key);
                    if (saved) {
                        return saved;
                    }
                    // Nový sloupec, který není v uložených preferencích
                    return {
                        ...col,
                        isAvailable: true,
                        isDefault: false,
                    };
                });
                setSettings(mergedSettings);
            } else {
                // Žádné uložené nastavení, použijí se výchozí
                setSettings(getDefaultExportColumnSettings());
            }

            setIsLoading(false);
        };
        loadSettings();
    }, [user]);

    const handleSettingChange = (key: string, field: 'isAvailable' | 'isDefault', value: boolean) => {
        setSettings(currentSettings =>
            currentSettings.map(setting => {
                if (setting.key === key) {
                    const newSetting = { ...setting, [field]: value };
                    // If we make a column unavailable, it cannot be a default
                    if (field === 'isAvailable' && !value) {
                        newSetting.isDefault = false;
                    }
                    // If we make a column a default, it must be available
                    if (field === 'isDefault' && value) {
                        newSetting.isAvailable = true;
                    }
                    return newSetting;
                }
                return setting;
            })
        );
    };

    const handleSave = async () => {
        if (!user) return;
        setIsSaving(true);
        // FIX: Replaced storageService with apiService
        await apiService.setPreference(EXPORT_COLUMNS_PREF_KEY, settings);
        setIsSaving(false);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
    };

    if (isLoading) {
        return <Spinner text="Načítám nastavení exportu..." />;
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">{title}</h3>
            <p className="text-gray-600 mb-6 text-sm">Zde můžete nastavit, které sloupce budou dostupné v dialogu pro export a které budou ve výchozím stavu vybrány.</p>

            <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 font-semibold text-gray-600 px-4">
                    <span>Název sloupce</span>
                    <span className="text-center">Zobrazit v dialogu</span>
                    <span className="text-center">Výchozí výběr</span>
                </div>
                {settings.map(setting => (
                    <div key={setting.key} className="grid grid-cols-3 gap-4 items-center p-4 border rounded-md hover:bg-gray-50">
                        <label htmlFor={`available-${setting.key}`} className="text-gray-800">{setting.label}</label>
                        <div className="flex justify-center">
                            <input
                                type="checkbox"
                                id={`available-${setting.key}`}
                                checked={setting.isAvailable}
                                onChange={(e) => handleSettingChange(setting.key, 'isAvailable', e.target.checked)}
                                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex justify-center">
                            <input
                                type="checkbox"
                                id={`default-${setting.key}`}
                                checked={setting.isDefault}
                                onChange={(e) => handleSettingChange(setting.key, 'isDefault', e.target.checked)}
                                disabled={!setting.isAvailable}
                                className="h-5 w-5 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:opacity-50"
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-8 flex items-center gap-4">
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
                >
                    {isSaving ? 'Ukládám...' : 'Uložit nastavení'}
                </button>
                 {showSuccess && (
                    <span className="text-green-600 font-medium transition-opacity duration-300">
                        Nastavení uloženo!
                    </span>
                )}
            </div>
        </div>
    );
};